/**
 * The HDR render core.
 *
 * The scene draws into a 640x360 half-float target with nearest filtering, and
 * that target is the only place gameplay geometry exists. Everything after it is
 * a fullscreen pass. Two rules hold the pixel look together:
 *
 *  - Y is flipped exactly once, in the orthographic camera, so buffer space
 *    (origin top-left, y down) maps 1:1 onto world units and no subsystem ever
 *    has to think about handedness again.
 *  - The buffer covers the *region*, not the 640x360 band. On a window that is
 *    not 16:9 the camera is widened or heightened to the window's own shape with
 *    the band centred inside it, so the glass carries lattice instead of a black
 *    bar. `core/region.ts` owns that rule; everything here just obeys it, and
 *    one world unit is still one buffer pixel however far the slice extends.
 *  - The output fills the tube, and it gets there in two steps: an integer
 *    nearest prescale, then one linear resolve of whatever fraction is left.
 *    That is the emulator `sharp-bilinear` filter. Flooring the whole fit to an
 *    integer instead would leave a 1836x1034 window lit across 1280x720 of
 *    itself — half the glass switched off — and a raw 2.84x nearest blowup
 *    would give some source pixels three screen pixels and some two, which is
 *    the uneven grid `docs/ARCHITECTURE.md` calls a blurry mistake.
 */

import * as THREE from 'three';
import { QUALITY, SIM, VIEW } from '@/core/config';
import type { QualityTier } from '@/core/config';
import { PHOSPHOR } from '@/core/palette';
import { perf } from '@/core/perf';
import { bandBoxIn, region, regionFor } from '@/core/region';
import type { Box } from '@/core/region';
import { FullscreenPass } from './passes/FullscreenPass';
import { PostStack } from './PostStack';

/**
 * The prescale blit. Deliberately the dumbest shader in the project: the whole
 * filter lives in the target's sampler state, not here.
 */
const BLIT_FRAG = /* glsl */ `
precision highp float;
uniform sampler2D tSrc;
in vec2 vUv;
out vec4 fragColor;
void main() { fragColor = texture(tSrc, vUv); }
`;

export interface RendererOptions {
  canvas?: HTMLCanvasElement;
  quality?: QualityTier;
}

/**
 * A layer with per-frame state of its own that has to be advanced before the
 * scene draws — a procedural background, a canvas, a video.
 *
 * Declared here rather than imported so `Renderer` keeps no dependency on the
 * implementation. `HydraBackground` is the only one today, and it wants its
 * clock from this object; going the other way and having `Renderer` import it
 * would make the render core know about a layer that is pure decoration.
 */
export interface BackgroundLayer {
  /**
   * Advance to `timeSeconds` and refresh whatever the scene samples.
   *
   * The time handed in is derived from the frame counter, never from the wall
   * clock, so the capture harness gets the same pixels on every run.
   */
  update(timeSeconds: number): void;
}

export interface RenderStats {
  /** GPU milliseconds for the last completed timer query, or 0 when unsupported. */
  gpuMs: number;
  /** Wall milliseconds spent submitting the frame. */
  cpuMs: number;
  frame: number;
  drawCalls: number;
  triangles: number;
  programs: number;
  /** Output pixels per buffer pixel. */
  scale: number;
  outputWidth: number;
  outputHeight: number;
  pixelRatio: number;
  tier: QualityTier;
  /** False when the driver refused half-float colour and we fell back to 8-bit. */
  hdr: boolean;
  gpuTimerSupported: boolean;
  /** Ladder rungs the governor is currently holding down. 0 = the full tier. */
  perfRung: number;
}

interface TimerExtension {
  readonly TIME_ELAPSED_EXT: number;
  readonly GPU_DISJOINT_EXT: number;
}

/**
 * `EXT_disjoint_timer_query_webgl2` in a form that will not stall the pipeline.
 * Results are collected some frames after submission; a disjoint event throws
 * the whole in-flight set away rather than reporting a number nobody can trust.
 */
class GpuTimer {
  private readonly gl: WebGL2RenderingContext;
  private readonly ext: TimerExtension | null;
  private readonly free: WebGLQuery[] = [];
  private readonly pending: WebGLQuery[] = [];
  private active: WebGLQuery | null = null;
  /** Milliseconds from the most recent query that came back clean. */
  last = 0;

  constructor(gl: WebGL2RenderingContext) {
    this.gl = gl;
    this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as TimerExtension | null;
  }

  get supported(): boolean {
    return this.ext !== null;
  }

  begin(): void {
    if (this.ext === null || this.active !== null) return;
    // Four in flight is plenty; beyond that the results are too stale to act on.
    if (this.pending.length >= 4 && this.free.length === 0) return;
    const query = this.free.pop() ?? this.gl.createQuery();
    if (query === null) return;
    this.active = query;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
  }

  end(): void {
    if (this.ext === null || this.active === null) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.pending.push(this.active);
    this.active = null;
  }

  poll(): void {
    if (this.ext === null || this.pending.length === 0) return;

    if (this.gl.getParameter(this.ext.GPU_DISJOINT_EXT) === true) {
      for (const query of this.pending) this.gl.deleteQuery(query);
      this.pending.length = 0;
      return;
    }

    while (this.pending.length > 0) {
      const query = this.pending[0];
      if (this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE) !== true) break;
      this.last = (this.gl.getQueryParameter(query, this.gl.QUERY_RESULT) as number) / 1e6;
      this.pending.shift();
      this.free.push(query);
    }
  }

  dispose(): void {
    for (const query of this.pending) this.gl.deleteQuery(query);
    for (const query of this.free) this.gl.deleteQuery(query);
    this.pending.length = 0;
    this.free.length = 0;
  }
}

let warnedNoHalfFloat = false;

export class Renderer {
  readonly gl: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.OrthographicCamera;
  readonly post: PostStack;
  readonly stats: RenderStats;
  readonly canvas: HTMLCanvasElement;

  /** Interpolation factor handed to the last `render` call, for render systems. */
  alpha = 0;

  /**
   * Optional layer refreshed at the top of every frame. `main` assigns it.
   *
   * It is driven from here rather than from `main`'s render pass because the
   * clock it needs — the frame counter — is this object's, and routing it back
   * out would give the capture harness a second way to get it wrong.
   */
  background: BackgroundLayer | null = null;

  private readonly sceneTarget: THREE.WebGLRenderTarget;
  /** Buffer at an integer multiple, sampled nearest going in and linear coming out. */
  private readonly prescaleTarget: THREE.WebGLRenderTarget;
  private readonly blit: FullscreenPass;
  private readonly timer: GpuTimer;
  private tier: QualityTier;
  private frame = 0;
  private outputWidth: number = VIEW.width;
  private outputHeight: number = VIEW.height;
  private scale = 1;
  private prescale = 1;
  /** True when the fit left a fraction over the integer prescale to resolve. */
  private sharpFit = false;
  private pixelRatio = 1;
  /** Last governor generation acted on, so a rung change resizes exactly once. */
  private perfGeneration = perf.generation;
  /**
   * True while a draw is in flight. `render` resizes on a rung change and
   * `resize` repaints after it moves the drawing buffer, so without this the two
   * would call each other into a second, wasted frame.
   */
  private drawing = false;

  private resizeObserver: ResizeObserver | null = null;
  private dprQuery: MediaQueryList | null = null;
  /** Trailing debounce for region reallocation during a window drag. */
  private regionTimer = 0;
  /** False until resize() has applied a region once; boot applies immediately. */
  private regionApplied = false;

  private readonly onResize = (): void => this.resize();
  private readonly onDprChange = (): void => {
    this.watchPixelRatio();
    this.resize();
  };

  constructor(options: RendererOptions = {}) {
    const canvas = options.canvas ?? (document.getElementById('gl') as HTMLCanvasElement | null);
    if (canvas === null) throw new Error('Renderer: no canvas. Pass one, or add #gl to the document.');
    this.canvas = canvas;
    this.tier = options.quality ?? 'ultra';

    this.gl = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      alpha: false,
      depth: false,
      stencil: false,
      powerPreference: 'high-performance',
      // The capture harness screenshots outside the animation frame, so the
      // default framebuffer has to survive past the compositor's swap.
      preserveDrawingBuffer: true,
      failIfMajorPerformanceCaveat: false,
    });

    // three r180 only ever creates a WebGL2 context; the union in the typings
    // is a leftover from when WebGL1 was still an option.
    const context = this.gl.getContext() as WebGL2RenderingContext;
    // Half-float colour attachments are what let emissive values run past 1.0.
    // Without them the bright pass has nothing above threshold to work with.
    const hdr =
      context.getExtension('EXT_color_buffer_half_float') !== null ||
      context.getExtension('EXT_color_buffer_float') !== null;
    if (!hdr && !warnedNoHalfFloat) {
      warnedNoHalfFloat = true;
      console.warn(
        'Renderer: EXT_color_buffer_half_float missing. Falling back to 8-bit targets; ' +
          'bloom and halation will be far weaker than intended.',
      );
    }
    const type = hdr ? THREE.HalfFloatType : THREE.UnsignedByteType;

    // Before any target is sized: everything below covers the region, and the
    // region is a property of the window rather than of `VIEW`. `resize` recomputes
    // this on every observer callback; the constructor needs it once, early.
    const host = this.measureHost();
    region.set(regionFor(host.width, host.height));
    const view = region.current;

    this.gl.autoClear = false;
    this.gl.info.autoReset = false;
    this.gl.toneMapping = THREE.NoToneMapping;
    // The composite encodes sRGB by hand as its last operation, so three must
    // not convert anything on the way out.
    this.gl.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.gl.setClearColor(
      new THREE.Color().setRGB(PHOSPHOR.black[0], PHOSPHOR.black[1], PHOSPHOR.black[2], THREE.LinearSRGBColorSpace),
      1,
    );

    this.sceneTarget = new THREE.WebGLRenderTarget(view.width, view.height, {
      // RGBA rather than RGB: RGB half-float is not a renderable format on a
      // good share of drivers, and the alpha channel costs nothing here.
      format: THREE.RGBAFormat,
      type,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      generateMipmaps: false,
      // No depth *texture*, but a depth buffer stays: sprite layering relies on it.
      depthBuffer: true,
      stencilBuffer: false,
      colorSpace: THREE.LinearSRGBColorSpace,
    });
    this.sceneTarget.texture.name = 'scene';

    // The two filters differ on purpose, and the difference is the whole trick.
    //
    //   magFilter = Linear  — the composite magnifies this texture by the
    //     leftover fraction (2.84 / 2 = 1.42x). Bilinear across an image that
    //     is already an exact 2x nearest blowup softens at most one output
    //     pixel per source edge and keeps every source pixel the same size.
    //   minFilter = Nearest — bloom and persistence *minify* it back toward
    //     buffer resolution. Every 2x2 block here holds one repeated source
    //     texel, so point-sampling returns exactly what those passes read
    //     before this target existed. The glow chain does not shift.
    this.prescaleTarget = new THREE.WebGLRenderTarget(view.width, view.height, {
      format: THREE.RGBAFormat,
      type,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      generateMipmaps: false,
      depthBuffer: false,
      stencilBuffer: false,
      colorSpace: THREE.LinearSRGBColorSpace,
    });
    this.prescaleTarget.texture.name = 'prescale';
    this.blit = new FullscreenPass(BLIT_FRAG, { tSrc: { value: null } });

    // Buffer space: origin top-left, y down, one world unit per buffer pixel.
    //
    // The flip lives on the scene transform, not in the projection matrix. Both
    // produce the same image, but a negative scale in the projection reverses
    // triangle winding and three has no idea it happened, so every front-facing
    // sprite gets back-face culled and the buffer comes out empty. On an object
    // transform three checks `matrixWorld.determinant()` and flips the cull
    // convention itself. This is the single Y flip in the project; add meshes to
    // `scene` in buffer coordinates and they land where you expect.
    this.scene = new THREE.Scene();
    this.scene.scale.set(1, -1, 1);
    this.scene.position.set(0, VIEW.height, 0);

    // Frustum from the region, not from `VIEW`. The scene transform above is
    // deliberately left at the band's own height: it is the Y flip and nothing
    // else, and `applyRegion` compensates for it in the camera's top and bottom
    // rather than moving the whole scene every time the window changes shape.
    this.camera = new THREE.OrthographicCamera(0, VIEW.width, VIEW.height, 0, -1000, 1000);
    this.camera.position.set(0, 0, 100);
    this.applyRegion();

    this.post = new PostStack(this.gl, {
      width: view.width,
      height: view.height,
      type,
      quality: this.tier,
    });

    this.timer = new GpuTimer(context);

    this.stats = {
      gpuMs: 0,
      cpuMs: 0,
      frame: 0,
      drawCalls: 0,
      triangles: 0,
      programs: 0,
      scale: 1,
      outputWidth: VIEW.width,
      outputHeight: VIEW.height,
      pixelRatio: 1,
      tier: this.tier,
      hdr,
      gpuTimerSupported: this.timer.supported,
      perfRung: 0,
    };

    canvas.style.display = 'block';
    canvas.style.margin = 'auto';
    canvas.style.imageRendering = 'pixelated';

    this.attachObservers();
    this.resize();
  }

  /** The HDR buffer the scene lands in. Render systems never need it; tools do. */
  get sceneTexture(): THREE.Texture {
    return this.sceneTarget.texture;
  }

  /** The adaptive governor. Exposed so tools and the HUD can read its state. */
  get governor(): typeof perf {
    return perf;
  }

  setQuality(tier: QualityTier): void {
    if (tier === this.tier) return;
    this.tier = tier;
    this.stats.tier = tier;
    // A tier the player picked is the new ceiling, and the ladder starts from
    // it. Carrying rungs across would stack one degradation on another and hand
    // back something below what they asked for.
    perf.reset();
    this.post.setQuality(tier);
    // The tier caps device pixel ratio, so the output geometry moves with it.
    this.resize(true);
  }

  /**
   * Recomputes output geometry. Cheap and idempotent — safe to call from a
   * resize observer, a DPR change, or a quality switch.
   */
  resize(force = false): void {
    const host = this.measureHost();
    // Shape the slice to the window first: everything below is a fit of that
    // slice, and on a window that is not 16:9 it is a different slice than it
    // was a moment ago. A region change forces the geometry work through even
    // when the output box lands on the same number of pixels it had before.
    //
    // The region half is debounced behind a trailing timer, and only when a
    // region already exists. Applying it reallocates every render target in
    // the chain — measured on a live drag: 12 textures plus a depth
    // renderbuffer per change, a change every 16px of drag, 240 texImage2D
    // calls over 2.5 seconds, roughly 280MB of VRAM churn for one hand
    // gesture. The output fit below stays per-event, so the window still
    // tracks the drag; the world slice catches up 120ms after the hand stops,
    // and until then the old slice letterboxes into the new box.
    const want = regionFor(host.width, host.height);
    const cur = region.current;
    let moved = false;
    if (want.width !== cur.width || want.height !== cur.height) {
      if (!this.regionApplied) {
        // Boot: the first real region applies immediately, whatever the
        // window's shape — a startup should not blink through the base slice.
        region.set(want);
        this.applyRegion();
        this.regionApplied = true;
        moved = true;
      } else {
        if (this.regionTimer !== 0) window.clearTimeout(this.regionTimer);
        this.regionTimer = window.setTimeout(() => {
          this.regionTimer = 0;
          const late = this.measureHost();
          if (region.set(regionFor(late.width, late.height))) {
            this.applyRegion();
            this.resize(true);
          }
        }, 120);
        // The deferred slice has not applied yet; nothing downstream moved.
      }
    }
    const view = region.current;

    // The governor's last rung caps this too, and it only ever caps: the tier
    // remains the ceiling however hard the frame is struggling.
    const cap = Math.min(QUALITY[this.tier].maxPixelRatio, perf.caps.pixelRatioCap);
    const dpr = Math.min(typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1, cap);

    const availW = Math.max(1, Math.floor(host.width * dpr));
    const availH = Math.max(1, Math.floor(host.height * dpr));

    // The tube gets the whole fit, not the floor of it. Flooring 2.84 to 2 here
    // is what used to leave 273px of dead glass down each side.
    const scale = Math.max(Math.min(availW / view.width, availH / view.height), 0.2);
    // One integer step of nearest, taken before anything fractional happens.
    const prescale = Math.max(1, Math.floor(scale));

    const outputWidth = Math.max(1, Math.floor(view.width * scale));
    const outputHeight = Math.max(1, Math.floor(view.height * scale));
    // An exact integer fit needs no resolve; skip the blit and stay pure nearest.
    const sharpFit = scale > 1 && outputWidth !== view.width * prescale;

    if (
      !force &&
      !moved &&
      outputWidth === this.outputWidth &&
      outputHeight === this.outputHeight &&
      dpr === this.pixelRatio
    ) {
      return;
    }

    this.outputWidth = outputWidth;
    this.outputHeight = outputHeight;
    this.scale = scale;
    this.sharpFit = sharpFit;
    if (moved || prescale !== this.prescale) {
      this.prescale = prescale;
      this.prescaleTarget.setSize(view.width * prescale, view.height * prescale);
    }
    this.pixelRatio = dpr;

    // Drive the drawing buffer directly and describe it to CSS afterwards.
    // Going through three's pixelRatio would reintroduce rounding we just
    // spent the whole function removing.
    this.gl.setPixelRatio(1);
    this.gl.setSize(outputWidth, outputHeight, false);
    this.gl.setViewport(0, 0, outputWidth, outputHeight);
    this.canvas.style.width = `${outputWidth / dpr}px`;
    this.canvas.style.height = `${outputHeight / dpr}px`;

    this.post.resize(view.width, view.height, outputWidth, outputHeight, scale);

    this.stats.scale = scale;
    this.stats.outputWidth = outputWidth;
    this.stats.outputHeight = outputHeight;
    this.stats.pixelRatio = dpr;

    /*
     * Repaint, because the line above that changed the drawing buffer's size
     * emptied it.
     *
     * Resizing a canvas clears it — that is the HTML spec, not a driver quirk —
     * and this function runs from a resize observer, outside any frame. Under
     * the animation loop the next frame lands 16ms later and nobody sees the
     * gap. Nothing else is so lucky: `advanceFrames` renders synchronously and
     * then stops, so a resize arriving after the last of them leaves a blank
     * tube with no frame coming to fill it. That is what a portrait capture
     * photographed — mean luminance exactly 0, the DOM damage floaters still
     * drawn over it, and a single extra `render` enough to bring the whole
     * picture back. It only shows now because the region is adaptive: until the
     * playfield box could stop being 16:9, the HUD bands settling late never
     * moved the canvas.
     */
    if (this.frame > 0 && !this.drawing) this.present();
  }

  /**
   * Scene into the HDR buffer, post chain to the canvas. The drawing half of a
   * frame and nothing else — no clock, no frame counter, no measurement — so
   * `resize` can use it to repaint a canvas it just emptied without moving the
   * deterministic clock a capture depends on.
   */
  private present(): void {
    this.gl.setRenderTarget(this.sceneTarget);
    this.gl.clear(true, true, false);
    this.gl.render(this.scene, this.camera);

    let sceneTexture = this.sceneTarget.texture;
    if (this.sharpFit) {
      this.blit.uniforms.tSrc.value = sceneTexture;
      this.blit.render(this.gl, this.prescaleTarget);
      sceneTexture = this.prescaleTarget.texture;
    }

    this.post.render(sceneTexture);
  }

  /**
   * Draws the scene into the HDR buffer, then runs the post chain to the canvas.
   * `alpha` is the fixed-step interpolation factor; render systems read it back
   * off this object when they sync.
   */
  render(alpha: number): void {
    this.alpha = alpha;
    this.frame++;
    // Held for the whole frame: the rung check below can resize, and `resize`
    // repaints, which from in here would be a second draw of the same frame.
    this.drawing = true;

    const cpuStart = performance.now();
    // The governor may have moved the pixel ratio cap since the last frame.
    // `resize` is idempotent and early-outs when the geometry has not moved.
    if (this.perfGeneration !== perf.generation) {
      this.perfGeneration = perf.generation;
      this.stats.perfRung = perf.level;
      this.resize();
    }
    this.gl.info.reset();
    this.timer.poll();
    this.timer.begin();

    // Noise and glare advance on the frame counter, not on wall time, so a
    // capture of frame N is byte-identical between runs.
    this.post.time = this.frame / SIM.hz;
    // Before the scene draw, and on the same deterministic clock, so whatever
    // the layer produces is this frame's rather than the previous one's.
    this.background?.update(this.frame / SIM.hz);

    this.present();

    this.timer.end();

    this.stats.frame = this.frame;
    this.stats.cpuMs = performance.now() - cpuStart;
    this.stats.gpuMs = this.timer.last;
    // Measured from the frame's start, not its end: the interval between two
    // starts is the frame period, and the governor wants the period as well as
    // the work. A zero `last` means no query has come back clean yet.
    perf.sample(this.timer.last, this.stats.cpuMs, this.timer.supported && this.timer.last > 0, cpuStart);
    this.stats.drawCalls = this.gl.info.render.calls;
    this.stats.triangles = this.gl.info.render.triangles;
    this.stats.programs = this.gl.info.programs?.length ?? 0;
    this.drawing = false;
  }

  /** Wipes the persistence feedback. Call before a deterministic capture. */
  clearHistory(): void {
    this.post.clearHistory();
  }

  dispose(): void {
    this.detachObservers();
    if (this.regionTimer !== 0) window.clearTimeout(this.regionTimer);
    this.timer.dispose();
    this.post.dispose();
    this.blit.dispose();
    this.prescaleTarget.dispose();
    this.sceneTarget.dispose();
    this.gl.dispose();
  }

  /**
   * Points the camera and the HDR buffer at the current region.
   *
   * The scene carries the Y flip as a `-1` scale about `VIEW.height`, so a world
   * point `y` arrives at `VIEW.height - y` before the projection sees it. The
   * region's top edge `y0` therefore lands at `VIEW.height - y0` and its bottom
   * one edge-height below, which is the whole of the arithmetic here. Leaving the
   * flip alone rather than folding the offset into `scene.position` keeps the
   * single-Y-flip rule this file opens with intact.
   */
  private applyRegion(): void {
    const view = region.current;
    this.camera.left = view.x0;
    this.camera.right = view.x0 + view.width;
    this.camera.top = VIEW.height - view.y0;
    this.camera.bottom = VIEW.height - view.y0 - view.height;
    this.camera.updateProjectionMatrix();
    this.sceneTarget.setSize(view.width, view.height);
  }

  /**
   * The 640x360 band's box in client coordinates, for DOM placed against game
   * space — damage floaters, tile centres, a touch radius quoted in CSS pixels.
   *
   * The canvas box is the *region*, so it is the wrong rectangle for any of
   * those the moment the window stops being 16:9. Identical to the canvas rect
   * whenever the region is the bare band, which is why every caller of
   * `canvas.getBoundingClientRect()` was right until now.
   *
   * A `DOMRect` rather than a plain box so it drops into the places that already
   * take one — `right` and `bottom` included, which the occlusion stamp reads.
   */
  viewRect(): DOMRect {
    const b: Box = bandBoxIn(this.canvas.getBoundingClientRect());
    return new DOMRect(b.left, b.top, b.width, b.height);
  }

  private measureHost(): { width: number; height: number } {
    const parent = this.canvas.parentElement;
    if (parent !== null) {
      const rect = parent.getBoundingClientRect();
      if (rect.width >= 1 && rect.height >= 1) return { width: rect.width, height: rect.height };
    }
    if (typeof window === 'undefined') return { width: VIEW.width, height: VIEW.height };
    return { width: window.innerWidth, height: window.innerHeight };
  }

  private attachObservers(): void {
    if (typeof window === 'undefined') return;
    const parent = this.canvas.parentElement;
    if (parent !== null && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(parent);
    }
    window.addEventListener('resize', this.onResize);
    this.watchPixelRatio();
  }

  private detachObservers(): void {
    if (typeof window === 'undefined') return;
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener('resize', this.onResize);
    this.dprQuery?.removeEventListener('change', this.onDprChange);
    this.dprQuery = null;
  }

  /**
   * Dragging the window between a retina and a non-retina display fires no
   * resize event, only a media query change — and the match has to be rebuilt
   * against the new ratio each time it trips.
   */
  private watchPixelRatio(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    this.dprQuery?.removeEventListener('change', this.onDprChange);
    const dpr = window.devicePixelRatio || 1;
    this.dprQuery = window.matchMedia(`(resolution: ${dpr}dppx)`);
    this.dprQuery.addEventListener('change', this.onDprChange);
  }
}
